// Critical-fields contract — see aegle-docs critical_fields_contract.md
import { Box, Table, TableHead, TableRow, TableCell, TableBody, Typography } from "@mui/material";
import type { HeaderMapping } from "../../utils/fieldMappingUtils";
import { CrossSchemaMappingInfo, MappingRow } from "./MappingRow";
import { SchemaFieldDefinition } from "@/constants/schema-fields/schemaFields";
import { useMemo } from "react";
import { CriticalFieldEntry } from "../document-review/criticalFields";

interface MappingTableProps {
  headerMappings: HeaderMapping[];
  editedMappings: Record<string, string[]>;
  availableFields: SchemaFieldDefinition[];
  /** Critical-field entries for the current schema. Empty for non-mappable schemas. */
  criticalFields: CriticalFieldEntry[];
  /**
   * Per-header lookup of mappings the same detected header has in OTHER
   * schemas. Surfaces the "also mapped in {Schema}" chip on the row so
   * reviewers see at a glance that the header is doing work elsewhere.
   */
  crossSchemaMappingsByHeader?: Map<string, CrossSchemaMappingInfo[]>;
  onMappingChange: (header: string, targetFields: string[]) => void;
  duplicateTargets?: Set<string>;
  /**
   * Override the target-column header — defaults to "Maps to". Callers pass
   * a schema-aware variant ("Maps to (Claimants)") so the per-tab scope is
   * unambiguous in the table header.
   */
  targetColumnLabel?: string;
}

/**
 * Table displaying all header mappings with editable field assignments
 */
export const MappingTable = ({
  headerMappings,
  editedMappings,
  availableFields,
  criticalFields,
  crossSchemaMappingsByHeader,
  onMappingChange,
  duplicateTargets = new Set(),
  targetColumnLabel = "Maps to",
}: MappingTableProps) => {
  // De-dup by header name AND sort by mapping tier so the reviewer's eye lands
  // on completed work first, then on what still needs attention:
  //   tier 0 — mapped here (top — done in this tab)
  //   tier 1 — unmapped here AND unmapped in any other schema (middle — top of
  //            the "unmapped" section, this is what still needs attention)
  //   tier 2 — mapped only in another schema (bottom — already handled elsewhere)
  // Stable sort preserves the carrier-source order within each tier.
  const orderedMappings = useMemo(() => {
    const seen = new Set<string>();
    const dedup = headerMappings.filter((m) => {
      if (seen.has(m.detectedHeader)) return false;
      seen.add(m.detectedHeader);
      return true;
    });
    const tierFor = (header: string): number => {
      const isMappedHere = (editedMappings[header]?.length ?? 0) > 0;
      const isMappedElsewhere = (crossSchemaMappingsByHeader?.get(header)?.length ?? 0) > 0;
      if (isMappedHere) return 0;
      if (isMappedElsewhere) return 2;
      return 1;
    };
    return [...dedup].sort((a, b) => tierFor(a.detectedHeader) - tierFor(b.detectedHeader));
  }, [headerMappings, editedMappings, crossSchemaMappingsByHeader]);

  // Per-schema set of every target field already mapped across any header.
  // Each row's dropdown filters this down to "fields mapped by OTHER rows" so
  // a target can't be picked twice; the row's own selections stay visible.
  const claimedFieldsAcrossRows = useMemo(() => {
    const set = new Set<string>();
    for (const fields of Object.values(editedMappings)) {
      for (const f of fields) set.add(f);
    }
    return set;
  }, [editedMappings]);

  if (orderedMappings.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No detected headers in this document.</Typography>
      </Box>
    );
  }

  return (
    <Table size="small" sx={{ "& .MuiTableCell-root": { borderBottomColor: "divider" } }}>
      <TableHead>
        <TableRow>
          <TableCell
            sx={{ color: "text.secondary", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Detected Header
          </TableCell>
          <TableCell
            sx={{ color: "text.secondary", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            Sample Value
          </TableCell>
          <TableCell
            sx={{ color: "text.secondary", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 0.5 }}
          >
            {targetColumnLabel}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {orderedMappings.map((mapping, index) => {
          const currentMapping = editedMappings[mapping.detectedHeader] || [];
          const isDuplicate = currentMapping.some((field) => duplicateTargets.has(field));
          // Other rows' claims = all claimed fields minus this row's own
          // current selections. The row's dropdown then offers everything in
          // the schema except what other rows have already taken.
          const ownSelection = new Set(currentMapping);
          const rowAvailableFields = availableFields.filter(
            (f) => ownSelection.has(f.field) || !claimedFieldsAcrossRows.has(f.field)
          );

          return (
            <MappingRow
              key={`${mapping.detectedHeader}-${index}`}
              mapping={mapping}
              currentMapping={currentMapping}
              availableFields={rowAvailableFields}
              criticalFields={criticalFields}
              crossSchemaMappings={crossSchemaMappingsByHeader?.get(mapping.detectedHeader)}
              onMappingChange={onMappingChange}
              isDuplicate={isDuplicate}
            />
          );
        })}
      </TableBody>
    </Table>
  );
};
